import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/http';

// Contact correction (utility actions): a wrong phone / email means every
// SMS/WhatsApp/email bounces — the operator fixes it here and re-sends.
// `POST /v1/cases/sub-cases/:subCaseUrn/contact` → cases stamped write
// (`POST /v1/subcases/:id/contact`), audited with the acting operator.

export interface ContactPatch {
  email?: string;
  phone?: string;
  preferredChannel?: 'SMS' | 'WHATSAPP' | 'EMAIL' | 'PUSH';
}

export async function updateSubCaseContact(
  subCaseUrn: string,
  patch: ContactPatch,
): Promise<{ updated: true; subCaseUrn: string }> {
  return http
    .post(`v1/cases/sub-cases/${encodeURIComponent(subCaseUrn)}/contact`, { json: patch })
    .json<{ updated: true; subCaseUrn: string }>();
}

const updateKey = (caseUrn: string) => ['contact-update', caseUrn] as const;

/** A non-PII revision hides previous reveals when a contact write succeeds.
 * Values remain component-local; another audited click reads the new value. */
export function useContactRevision(caseUrn: string): string {
  return useMutationState({
    filters: { mutationKey: updateKey(caseUrn), status: 'success' },
    select: (mutation) => mutation.state.submittedAt,
  }).join(',');
}

/** Refresh persisted presence, not optimistic values copied from the form. */
export function useUpdateSubCaseContact(caseUrn: string, disruptionUrn?: string) {
  const qc = useQueryClient();
  return useMutation<{ updated: true; subCaseUrn: string }, Error, { subCaseUrn: string } & ContactPatch>({
    mutationKey: updateKey(caseUrn),
    mutationFn: ({ subCaseUrn, ...patch }) => updateSubCaseContact(subCaseUrn, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', caseUrn] });
      void qc.invalidateQueries({ queryKey: ['cases', caseUrn, 'passengers'] });
      void qc.invalidateQueries({ queryKey: ['cases', caseUrn, 'pnr-groups'] });
      void qc.invalidateQueries({ queryKey: ['cases', 'pnr-search'] });
      if (disruptionUrn) {
        void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'roster'] });
      } else {
        // Standalone/older group rows have no parent disruption identifier.
        void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'disruptions' && q.queryKey[2] === 'roster' });
      }
    },
  });
}
